import React, { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { secureGet } from "../config/api";
import { useTranslation } from "react-i18next";

import DynamicForm from "./DynamicForm";
import ProductForm from "../modules/Products/ProductForm";
import ClientForm from "../modules/Clients/ClientForm";
import PurchaseForm from "../modules/Purchases/PurchaseForm";
import OrderForm from "../modules/Orders/OrderForm";
import RequestForm from "../modules/Repo/RequestForm";
import CreditNoteForm from "../modules/CreditNotes/CreditNotesForm";
import AvoirFinancierForm from "../modules/AvoirFinanciers/AvoirFinancierForm";
import AvoirFournisseurForm from "../modules/AvoirFournisseurs/AvoirFournisseurForm";
import PaymentsForm from "../modules/Recouvrement/PaymentsForm";
import ExpenseForm from "../modules/Performance/ExpenseForm";
import QuoteForm from "../modules/Quotes/QuoteForm";
import { buildPaymentOptionsFromServer } from "../utils/paymentModes";
import { buildOptionsFromFrenchRows } from "../utils/paymentModesFr";
import imageIcon from "../icons/image.svg";
import SnackbarNotification from './SnackbarNotification';

function ModuleWrapper({ moduleType, mode = "add", onClose, onSuccess, initialValues: propInitialValues }) {
  const history = useHistory();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const getStoredValues = (key) => {
    const storedData = localStorage.getItem(key);
    return storedData ? JSON.parse(storedData) : {};
  };

  const [initialValues,] = useState(() => {
    if (moduleType === "settings") {
      return getStoredValues(moduleType);
    } else if (moduleType === "profile") {
      return getStoredValues("user");
    }
    return location.state?.initialValues || propInitialValues || {};
  });

  const getFormTitle = () => {
    return t(`form_titles.${mode === "modify" ? `edit_${moduleType}` : `add_${moduleType}`}`);
  };

  const [, setLoading] = useState(false);
  const [additionalFields, setAdditionalFields] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  const showSnackbar = (message, severity = 'success', manual = false) => {
    setSnackbar({ open: true, message, severity, manual });
  };
  
  const [dropdownOptions, setDropdownOptions] = useState({
    clients: [],
    repos: [],
    paymentMode: [],
    parentId: [],
    providers: [],
    products: [],
    families: [],
    brands: [],
    roles: [],
    cities: [],
    banks: [],
    expense_category: [],
    commercial: [],
    clientStatus: [],
    subscriptionPlans: [],
    billingInterval: [],
    hostingMode: [],
    invoices: [],
    tenants: [],
  });


  useEffect(() => {
    const ac = new AbortController();
    const signal = ac.signal;
    const fetchOptions = async () => {
      try {
        setLoading(true);
        const options = {};


        if (moduleType === "purchases") {
          const { data: providersData } = await secureGet("/providers", showSnackbar, { signal });
          options.providers = providersData.map((provider) => ({
            value: provider.id,
            label: provider.name,
          }));

          const { data: reposData } = await secureGet("/repos", showSnackbar, { signal });
          options.repos = reposData.map((repo) => ({
            value: repo.id,
            label: repo.name,
          }));

          const products = await secureGet("/products/unpaginated", showSnackbar, { signal });
          options.products = products.data.map((product) => ({
            value: product.id,
            reference: product.reference,
            name: product.name,
            fullEntity: product,
          }));
        }


        if (moduleType === "payments") {
          const { data: tenantsData } = await secureGet("/tenants", showSnackbar, { signal, params: { size: 500 } });
          options.tenants = (tenantsData?.content || tenantsData || []).map((t) => ({
            value: t.id ?? t.grayClientId,
            label: t.companyName || t.legalName || t.name || String(t.id),
          }));

          options.invoices = [];


          const { data: paymentData } = await secureGet("/payments/types", showSnackbar, { signal });
          options.paymentMode = paymentData.map((paymentMode) => ({
            value: paymentMode.id,
            label: paymentMode.name,
          }));

          const products = await secureGet("/banks", showSnackbar, { signal });
          options.banks = products.data.map((bank) => ({
            value: bank.id,
            label: bank.name,
          }));
        }



        if (moduleType === "users") {
          const { data: reposData } = await secureGet("/repos", showSnackbar, { signal });
          options.repos = reposData.map((repo) => ({
            value: repo.id,
            label: repo.name,
            active: repo.active,
          }));

          const roleKeys = Object.keys(t("roles", { returnObjects: true }) || {});
          options.roles = roleKeys.map((roleKey) => ({
            value: roleKey,
            label: t(`roles.${roleKey}`),
          }));
        }

        if (moduleType === "clients") {

          const { data: citiesData } = await secureGet("/cities", showSnackbar, { signal });
          options.cities = citiesData.map((city) => ({
            value: city.id,
            label: city.name,
          }));

          options.clientStatus = [     
            { value: "ACTIVE", label: t("fields.clients.status.ACTIVE") },
            { value: "PAST_DUE", label: t("fields.clients.status.PAST_DUE") },
            { value: "RESTRICTED", label: t("fields.clients.status.RESTRICTED") },
            { value: "SUSPENDED", label: t("fields.clients.status.SUSPENDED") },
            { value: "CANCELED", label: t("fields.clients.status.CANCELED") },
          ];

          options.subscriptionPlans = [
            { value: "ESSENTIAL", label: t("fields.clients.subscription.planName.BASIC") },
            { value: "GROWTH", label: t("fields.clients.subscription.planName.PRO") },
            { value: "ENTERPRISE", label: t("fields.clients.subscription.planName.ENTERPRISE") },
          ];
          options.billingInterval = [
            { value: "MONTHLY", label: t("fields.clients.subscription.billingInterval.MONTHLY") },
            { value: "YEARLY", label: t("fields.clients.subscription.billingInterval.YEARLY") },
          ];
          options.hostingMode = [
            { value: "SAAS", label: t("fields.clients.subscription.includedHostingMode.SAAS") },
            { value: "ON_PREMISE", label: t("fields.clients.subscription.includedHostingMode.ON_PREMISE") },
          ];
          options.subscriptionCancelReason = [
            { value: "CUSTOMER_REQUEST", label: t("fields.clients.subscription.cancelReason.CUSTOMER_REQUEST") },
            { value: "NOT_SATISFIED", label: t("fields.clients.subscription.cancelReason.NOT_SATISFIED") },
          ];
        }

        if (moduleType === "expenses") {
          const expenses = Object.keys(t("expense_types", { returnObjects: true }) || {});
          options.expense_category = expenses.map((roleKey) => ({
            value: roleKey,
            label: t(`expense_types.${roleKey}`),
          }));
        }

        setDropdownOptions((prevOptions) => ({ ...prevOptions, ...options }));
      } catch (error) {
        console.error("Error fetching dropdown options:", error);
      }
    };
    fetchOptions();

    return () => {
      ac.abort();
    };
  }, [moduleType]);

  const getClientFormFields = () => {
    return {
      baseFields: [
        { name: "companyName", label: t("signup_fields.companyName"), type: "text", required: true },
        { name: "companyPhone", label: t("signup_fields.companyPhone"), type: "text", required: true },
        { name: "contactFirstName", label: t("signup_fields.contactFirstName"), type: "text", required: true },
        { name: "contactLastName", label: t("signup_fields.contactLastName"), type: "text", required: true },
        { name: "contactEmail", label: t("signup_fields.contactEmail"), type: "text", required: true },
        {
          name: "preferredLanguage",
          label: t("signup_fields.preferredLanguage"),
          type: "dropdown",
          required: true,
          options: [
            { value: "fr", label: t("lang.fr") },
            { value: "en", label: t("lang.en") },
          ],
        },
        { name: "password", label: t("signup_fields.password"), type: "password", required: true },
        { name: "confirmPassword", label: t("signup_fields.confirmPassword"), type: "password", required: true },
      ],
      subscriptionFields: [],
      hostingFields: [],
    };
  };

  const getFieldSpecs = () => {
    switch (moduleType) {
      case "brands":
        return [
          { label: t(`fields.brands.name`), name: "name", required: true },
        ];
      case "families":
        return [
          { label: t(`fields.families.name`), name: "name", required: true },
          { label: t(`fields.families.parentId`), name: "parentId", type: "dropdown", options: dropdownOptions.parentId },
        ];
      case "orders":
        return [
          { label: t(`fields.orders.clientId`), name: "clientId", type: "dropdown", required: true, options: dropdownOptions.clients },
          { label: t(`fields.orders.repoId`), name: "repoId", type: "dropdown", required: true, options: dropdownOptions.repos },
          { label: t(`fields.orders.paymentMode`), name: "paymentMode", type: "dropdown", required: true, options: dropdownOptions.paymentMode },
          { label: t(`fields.orders.comment`), name: "comment", type: "textarea" },
        ];
      case "quotes":
        return [
          { label: t(`fields.orders.clientId`), name: "clientId", type: "dropdown", required: true, options: dropdownOptions.clients },
          { label: t(`fields.orders.repoId`), name: "repoId", type: "dropdown", required: true, options: dropdownOptions.repos },
          { label: t(`fields.orders.paymentMode`), name: "paymentMode", type: "dropdown", required: false, options: dropdownOptions.paymentMode },
        ];
      case "purchases":
        return [
          { label: t(`fields.payments.date_facture`), name: "dateFacture", type: "date", required: true },
          { label: t(`fields.purchases.factureNumber`), name: "factureNumber", required: true },
          { label: t(`fields.purchases.fournisseur`), name: "fournisseur", type: "dropdown", options: dropdownOptions.providers, required: true },
          { label: t(`fields.purchases.stockAlimenter`), name: "stockAlimenter", type: "dropdown", options: dropdownOptions.repos, required: true },
        ];
      case "users":
        return [
          { label: t(`fields.users.firstName`), name: "firstName", required: true },
          { label: t(`fields.users.lastName`), name: "lastName", required: true },
          { label: t(`fields.users.username`), name: "username", required: true },
          { label: t(`fields.users.email`), name: "email", required: true },
          {
            label: t(`fields.users.role`), name: "role", type: "dropdown", required: true,
            options: dropdownOptions.roles,
            conditionalFields: {
              ROLE_COMMERCIAL: [
                { name: "repositories", label: t('fields.users.repositories'), type: 'checkbox-dropdown', options: dropdownOptions.repos },
              ],
            },
          },
          { label: t(`fields.users.password`), name: "password", type: "password" },
          { label: t(`fields.users.passwordConfirmation`), name: "passwordConfirmation", type: "password", required: true, isPasswordConfirmation: true },
        ];
      case "repos":
        return [
          { label: t(`fields.repos.name`), name: "name", required: true },
        ];
      case "providers":
        return [
          { label: t(`fields.providers.name`), name: "name", required: true },
        ];
      case "userobj":
        return [
          { label: t(`fields.userobj.objectif`), name: "objectif", required: true },
        ];
      case "requests":
        return [
          { label: t(`fields.requests.sourceRepo`), name: "sourceRepo", type: "dropdown", required: true, options: dropdownOptions.sourceRepos },
          { label: t(`fields.requests.destRepo`), name: "destRepo", type: "dropdown", required: true, options: dropdownOptions.destRepos },
        ];
      case "profile":
        return [
          { label: t(`fields.users.lastName`), name: "lastName" },
          { label: t(`fields.users.firstName`), name: "firstName" },
          {
            label: t(`fields.users.language`), name: "preferredLanguage", type: "dropdown",
            options: [
              { label: t(`lang.fr`), value: "fr" },
              { label: t(`lang.en`), value: "en" }
            ],
            required: true
          },
        ];
      case "settings":
        return [
          { label: t(`fields.settings.grayName`), name: "companyName", required: true },
          { label: t(`fields.settings.fname`), name: "contactFirstName", required: true },
          { label: t(`fields.settings.lname`), name: "contactLastName", required: true },
          { label: t(`fields.settings.contactEmail`), name: "contactEmail" },
          { label: t(`fields.settings.phoneNumber`), name: "phoneNumber", required: true },
          {
            label: t(`fields.users.language`), name: "language", type: "dropdown",
            options: [
              { label: t(`lang.fr`), value: "fr" },
              { label: t(`lang.en`), value: "en" }
            ],
          },
          {
            label: t(`fields.settings.footer`),
            name: "footer",
            type: "text",
            isNormal: true
          },
        ];
      case "creditNotes":
        return [
          { label: t(`fields.orders.clientId`), name: "client", type: "dropdown", required: true, options: dropdownOptions.clients },
          { label: t(`fields.product`), name: "product", type: "dropdown", required: true, options: dropdownOptions.products },
        ]
      case "payments":
        return [
          { label: t(`fields.payments.grayClientId`), name: "grayClientId", type: "dropdown", required: true, options: dropdownOptions.tenants },
          { label: t(`fields.payments.billingInvoiceId`), name: "billingInvoiceId", type: "dropdown", options: dropdownOptions.invoices },
          { label: t(`fields.payments.paymentType`), name: "paymentTypeId", type: "dropdown", required: true, options: dropdownOptions.paymentMode },
          { label: t(`fields.payments.bank`), name: "bankId", type: "dropdown", options: dropdownOptions.banks },
          { label: t(`fields.payments.paymentNumber`), name: "paymentNumber", type: "text", required: true },
          { label: t(`fields.payments.amount`), name: "amount", type: "text", required: true },
          { label: t(`fields.payments.paymentDate`), name: "paymentDate", type: "date" },
          { label: t(`fields.payments.comment`), name: "comment", type: "text" },
        ];
      case "expenses":
        return [
          { label: "Notes", name: "comment", type: "textarea", required: false },
        ];
      case "afinancier":
        return [
          { label: t(`fields.payments.clientId`), name: "clientId", type: "dropdown", required: true, options: dropdownOptions.clients },
          { label: t(`columns.commercial`), name: "commercialId", type: "dropdown", required: true, options: dropdownOptions.commercial },
        ];
      case "afournisseur":
        return [
          { label: t(`fields.purchases.fournisseur`), name: "provider", type: "dropdown", options: dropdownOptions.providers, required: true },
          { label: t(`rows.repo`), name: "repo", type: "dropdown", required: true, options: dropdownOptions.sourceRepos },
        ];
      case "banks": 
        return [
          { label: t(`fields.banks.name`), name: "name", type: "text", required: true },
        ];
      case "paymentTypes":
        return [
          { label: t(`fields.paymentTypes.name`), name: "name", type: "text", required: true },
        ];
      default:
        return [];
    }
  };
  const getListFieldSpecs = () => {
    if (moduleType === "orders") {
      return [
        { label: t(`fields.list.product`), name: "product", type: "dropdown", required: true, options: dropdownOptions.products },
        { label: t(`fields.list.quantity`), name: "quantity", type: "number", required: true },
      ];
    } if (moduleType === "quotes") {
      return [
        { label: t(`fields.list.product`), name: "product", type: "dropdown", required: true, options: dropdownOptions.products },
        { label: t(`fields.list.quantity`), name: "quantity", type: "number", required: true },
      ];
    } else if (moduleType === "purchases") {
      return [
        { label: t(`fields.list.product`), name: "productName", type: "dropdown", options: dropdownOptions.products, required: true },
        { label: t(`fields.list.quantity`), name: "quantity", type: "number", required: true },
        { label: t(`fields.list.price`), name: "unitPrice", required: true },
      ];
    } else if (moduleType === "requests") {
      return [
        { label: t(`fields.list.product`), name: "product", type: "dropdown", options: dropdownOptions.products, required: true },
        { label: t(`fields.list.quantity`), name: "quantity", type: "number", required: true },
      ];
    } else if (moduleType === "expenses") {
      return [
        { label: t(`rows.category`), name: "category", type: "dropdown", options: dropdownOptions.expense_category, required: true },
        { label: t(`fields.payments.amount`), name: "price", required: true },
        { label: t(`fields.payments.other`), name: "comment", type: "text", required: false },
      ];
    } else if (moduleType === "afinancier") {
      return [
        { label: t(`rows.designation`), name: "designation", type: "text", required: true },
        { label: t(`fields.products.priceTTC`), name: "priceTTC", required: true },
      ];
    } else if (moduleType === "afournisseur") {
      return [
        { label: t(`fields.list.product`), name: "product", type: "dropdown", required: true, options: dropdownOptions.products },
      ];
    }
    return [];
  };

  const getRedirectUrl = () => {
    switch (moduleType) {
      case "brands":
        return "/catalog/brands";
      case "families":
        return "/catalog/families";
      case "orders":
        return "/orders";
      case "purchases":
        return "/purchases";
      case "users":
        return "/users";
      case "userobj":
        return "/users";
      case "repos":
        return "/repos";
      case "providers":
        return "/providers";
      case "products":
        return "/catalog/products";
      case "clients":
        return "/clients";
      case "requests":
        return "/transfer-requests";
      case "profile":
        return "/";
      case "settings":
        return "/";
      case "creditNotes":
        return "/credit-notes";
      case "quotes":
        return "/quotes";
      case "payments":
        return "/payments";
      case "afournisseur":
        return "/avoirs-fournisseurs";
      case "afinancier":
        return "/avoirs-financiers";
      case "invoices":
        return "/invoices";
      case "expenses":
        return "/expenses";
      case "banks":
        return "/banks";
      case "paymentTypes":
        return "/payment-types";
      default:
        return "/";
    }
  };

  const redirectUrl = getRedirectUrl();

  let fields = getFieldSpecs().map((field) => ({
    ...field,
    value: mode === "modify" ? initialValues[field.name] : "",
  }));

  if (mode === "modify" && moduleType === "users") {
    fields = fields.filter(field => field.name !== "password" && field.name !== "passwordConfirmation");
  }
  if (mode === "modify" && initialValues.id && moduleType !== "afinancier") {
    fields.push({ name: "id", value: initialValues.id, type: "hidden" });
  }

  const listFields = getListFieldSpecs().map((field) => ({
    ...field,
    value: mode === "modify" ? initialValues[field.name] : "",
  }));

  let fileFields = [];

  switch (moduleType) {
    case "brands":
    case "families":
      fileFields = [{
        name: "image",
        label: "",
        accept: "image/*",
        icon: <img src="/icons/image.svg" alt="Upload" className="w-8 h-8" />,
        placeholderText1: t("placeholder.upload_logo1"),
        placeholderText2: t("placeholder.upload_logo2")
      }];
      break;
    case "settings":
      fileFields = [
        {
          name: "image",
          label: t("fields.logoImage"),
          accept: "image/*",
          icon: <img src="/icons/image.svg" alt="Upload" className="w-8 h-8" />,
          placeholderText1: t("placeholder.upload_logo1"),
          placeholderText2: t("placeholder.upload_logo2")
        },
        {
          name: "header",
          label: t("fields.headerImage"),
          accept: "image/*",
          icon: <img src="/icons/image.svg" alt="Upload" className="w-8 h-8" />,
          placeholderText1: t("placeholder.upload_header"),
          placeholderText2: t("placeholder.upload_logo2")
        }
      ];
      break;
    case "payments":
      fileFields = [
        {
          name: "image",
          label: t("fields.paymentImage"),
          accept: "image/*",
          placeholderText1: t("placeholder.upload_logo1"),
          placeholderText2: t("placeholder.upload_logo2")
        }
      ];
      break;
    default:
      fileFields = [];
  }

  const clientFields = getClientFormFields();

  const apiUrl = moduleType === "paymentTypes" ? `/payments/types/add` : `${moduleType}/add`;

  const handleFormSubmit = async (responseData) => {
    if (!onClose) {
      history.push(redirectUrl);
    }
  };

  const handleSuccess = () => {
    const message = mode === 'modify'
      ? t(`confirmation.${moduleType}Edit`)
      : t(`confirmation.${moduleType}Add`);
    if (onSuccess) onSuccess(message);
    if (onClose) {
      onClose("submit");
    } else {
      history.push({
        pathname: redirectUrl,
        state: {
          showSnackbar: true,
          snackbarMessage: message,
          snackbarSeverity: 'success'
        }
      });
    }
  };

  const handleCancel = () => {

    if (onClose) {
      onClose("cancel");
    } else {
      history.push(redirectUrl);
    }
  };

  const renderPopup = () => {


    const formTitle = getFormTitle();
    if (moduleType === "brands" || moduleType === "families" || moduleType === "users" || moduleType === "repos" || moduleType === "providers" || moduleType === "userobj" || moduleType === "profile" || moduleType === "template" || moduleType === "settings" || moduleType === "banks" || moduleType === "paymentTypes") {
      return (
        <DynamicForm
          title={formTitle}
          initialValues={initialValues}
          baseFields={fields}
          additionalFields={additionalFields}
          listFields={listFields}
          products={dropdownOptions.products}
          fileFields={fileFields}
          onSubmit={handleFormSubmit}
          onSuccess={handleSuccess}
          onCancel={handleCancel}
          apiUrl={apiUrl}
          module={moduleType}

        />
      )
    }
  };

  const renderForm = () => {

    const formTitle = getFormTitle();


    if (moduleType === "clients") {
      return (
        <ClientForm
          title={formTitle}
          initialValues={initialValues}
          baseFields={clientFields.baseFields}
          subscriptionFields={clientFields.subscriptionFields}
          hostingFields={clientFields.hostingFields}
          additionalFields={additionalFields}
          paymentMode={dropdownOptions.paymentMode}
          onSuccess={handleSuccess}
          onCancel={handleCancel}
        />
      );
    } else if (moduleType === "purchases") {
      return (
        <PurchaseForm
          title={formTitle}
          baseFields={fields}
          listFields={listFields}
          additionalFields={additionalFields}
          products={dropdownOptions.products}
          onSubmit={handleFormSubmit}
          onSuccess={handleSuccess}
          onCancel={handleCancel}
        />
      )
    } else if (moduleType === 'expenses') {
      return (
        <ExpenseForm
          title={formTitle}
          baseFields={fields}
          listFields={listFields}
          onSuccess={handleSuccess}
          onCancel={handleCancel}
        />
      );
    } else if (moduleType === 'payments') {
      return (
        <PaymentsForm
          title={formTitle}
          baseFields={fields}
          fileFields={fileFields}
          onSuccess={handleSuccess}
          onCancel={handleCancel}
        />
      );

    }
  };


  return (
    <div>
      {renderPopup()}
      <div>{renderForm()}</div>
      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </div>
  );

}

export default ModuleWrapper;
